import { useParams, useSearch } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { PersonFormDialog } from "@/components/persons/PersonFormDialog";
import { PersonProfile } from "@/components/persons/PersonProfile";
import { EntityDetailPage } from "@/components/shared/entity-detail-page";
import { usePerson } from "@/hooks/use-persons";
import { useSession } from "@/lib/auth-client";
import { parseOriginSearch } from "@/lib/detail-origin";
import { canEditAll, canViewAll } from "@/lib/permissions";

export function PersonDetailPage() {
  const params = useParams({ strict: false });
  const personId = typeof params.personId === "string" ? params.personId : "";
  const query = usePerson(personId);
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  // Separate tiers: `isAdmin` gates EDIT affordances, `canView` gates the
  // read-only surfaces (change history, manager history). A view-only admin
  // must see both — the API already allows it, so gating them on the edit tier
  // would hide data the server would happily return.
  const canView = canViewAll(session?.user?.role);
  const [editing, setEditing] = useState(false);

  // Remount key for the edit dialog: refreshes the form's defaultValues when the
  // person changes externally (e.g. a scheduled change applied). Frozen WHILE the
  // dialog is open — editing the photo inside the dialog stamps a new updatedAt,
  // and an unfrozen key would remount the form and wipe the admin's typed edits.
  const formKeyRef = useRef(query.data?.updatedAt);
  if (!editing && query.data) formKeyRef.current = query.data.updatedAt;

  // `tab` lets a back-link (e.g. from a certification) reopen a specific tab.
  // The rest of the origin handling lives in EntityDetailPage; this is the same
  // pure read of the same search params.
  const parsed = parseOriginSearch(useSearch({ strict: false }));

  return (
    <EntityDetailPage
      query={query}
      entityLabel="Person"
      section="People & Organization"
      list={{ label: "People", to: "/dashboard/persons" }}
      name={query.data?.name}
      // The gap below the back link must be at least as tall as the pinned
      // header's ::before filler in `PersonProfile`, which mirrors main's
      // p-4 / md:p-6. That filler is opaque and sits directly above the sticky
      // block, so anything closer than its height gets painted over: at md the
      // filler is 24px against the default gap's 16px, which clipped the bottom
      // 8px of the back link. Keep these two in step if either changes.
      // Capped and centred so a wide screen leaves margin either side of the
      // panels rather than running them edge to edge. PersonProfile opts out of the
      // layout's narrower default (`data-wide-page`); this is the ceiling.
      className="mx-auto max-w-[84rem] md:space-y-6"
    >
      {(person) => (
        <>
          <PersonProfile
            // Remount on person→person navigation (same route, no natural remount)
            // so the active tab resets instead of leaking from the previous person.
            key={person.id}
            person={person}
            isAdmin={isAdmin}
            canView={canView}
            isSelf={!!person.userId && person.userId === session?.user?.id}
            onEdit={() => setEditing(true)}
            initialTab={parsed.tab}
          />

          {isAdmin && (
            <PersonFormDialog
              key={formKeyRef.current}
              open={editing}
              onOpenChange={setEditing}
              person={person}
            />
          )}
        </>
      )}
    </EntityDetailPage>
  );
}
